import { AbstractMessage, SenderDTO, MachineType, OutOfBandMessageDTO } from '../api';
import * as Events from './Events';
import * as SessionProvider from './SessionProvider';
import { DateUtils } from 'core/utils';
import * as Errors from './Errors';
import * as Localize from './Localize';
import { Log } from './Log';
import * as ConnectionChecker from './ConnectionChecker';
import * as Telemetry from './Telemetry';

const pendingRequests: AbstractMessage[] = [];

function addPendingRequest(request: AbstractMessage) {
	pendingRequests.push(request);
}

function removePendingRequest(request: AbstractMessage) {
	pendingRequests.splice(pendingRequests.indexOf(request), 1);
}

// Evito enviar nulls al servidor para que el comportamiento sea análogo al de Protocol Buffers,
// que no envía los nulls por lo que se quedan los valores por defecto establecidos en los
// constructores de los DTOs.
// Además, envío las horas como hora local, no como hora UTC porque si el browser y el servidor
// están en distinta zona horaria, yo quiero que todo se interprete "en crudo", sin hacer
// conversiones entre una y otra.
const DATE_REQUEST_REGEXP = new RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/);

const valueReplacer = (_key: any, value: any) => {
	if (value === null)
		return undefined;

	if (typeof value === 'string' && value.match(DATE_REQUEST_REGEXP)) {
		// Convierto otra vez a hora local y serializo
		const date = new Date(value);
		return DateUtils.formatDate(date, 'yyyy-MM-dd\'T\'HH:mm:ss.SSS');
	}

	return value;
};

type BusConfig = {
	applicationName: string,
	machineId: string,
	machineType: MachineType,
	posId: Nullable<number>,
	connectionCheckListener: ConnectionChecker.ConnectionCheckListener,
	outOfBandMessageProcessor: (messages: OutOfBandMessageDTO[]) => Promise<void>

};

let config: Nullable<BusConfig> = null;


async function process<T>(endpoint: string, request: AbstractMessage, convertResponse: (response: AbstractMessage) => T, signal?: AbortSignal): Promise<T> {

	const session = SessionProvider.isSessionStarted() ? SessionProvider.getCurrent() : null;

	if (config === null)
		throw Error('El Bus no ha sido inicializado');

	const sender: SenderDTO = {
		ApplicationName: config!.applicationName,
		ApplicationVersion: AGORA_VERSION,
		LanguageCode: Localize.getLanguageCode(),
		MachineId: config.machineId,
		MachineName: 'Web Device',
		MachineType: config.machineType,
		PosId: config.posId,
		PosName: session ? session.PosName : '',
		UserId: session ? session.UserId : null,
		UserName: session ? session.UserName : ''
	};

	request.Sender = sender;

	addPendingRequest(request);

	await Events.raise('request:started', request);

	try {

		Log.debug(`bus.request["${request.CLRType}"]`);

		const start = performance.now();

		if (ENABLE_POS_CONNECTION_CHECK)
			await ConnectionChecker.ensureServerIsAlive(config.connectionCheckListener);

		const response = await fetch(endpoint, {
			signal,
			method: 'post',
			headers: {
				'Content-type': 'application/json; charset=utf-8'
			},
			credentials: 'include',
			body: JSON.stringify({
				CLRType: request.CLRType,
				Message: request
			}, valueReplacer)
		});

		const end = performance.now();
		Log.debug(`bus.response["${request.CLRType}"]. Status: ${response.statusText} (${response.status}). Time: ${(end - start).toFixed(2)}ms`);

		Telemetry.request(request.CLRType, { duration: end - start });

		const serverError = await extractError(response);
		if (serverError)
			throw serverError;

		const responseMessage = await parseResponse(response);

		removePendingRequest(request);
		await Events.raise('request:completed', responseMessage);

		if (responseMessage.OutOfBandMessages.length > 0)
			await config.outOfBandMessageProcessor(responseMessage.OutOfBandMessages);

		return convertResponse(responseMessage);

	} catch (error: any) {

		const errorType = error[Errors.TypeSymbol];
		Log.warn(`bus.error[${request.CLRType}]. Type: ${errorType ?? 'unknown'}. Message: ${error.message ?? error.Message}`);

		removePendingRequest(request);

		await Events.raise('request:failed', error.message ?? error.Message);

		throw error;
	}
}

// eslint-disable-next-line no-useless-escape
const DATE_RESPONSE_REGEXP = new RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?([\+|-]\d{2}:\d{2})?$/);

async function parseResponse(response: Response): Promise<AbstractMessage> {

	const reviver = (_key: string, value: string) =>
		typeof value === 'string' && value.match(DATE_RESPONSE_REGEXP) ? DateUtils.parseDate(value) : value;

	const text = await response.text();
	return JSON.parse(text, reviver).Message as AbstractMessage;
}

async function extractError(response: Response): Promise<Nullable<string> | Error> {

	if (response.ok)
		return null;

	if (response.status === 500) {
		// Si es un error 500, el servidor de Ágora me habrá mandado un ErrorMessage
		// con más información del error
		const json = await response.json();
		const type = json.CLRType.split('.').reverse()[0];
		return createError(type, json.Message);
	}

	return new Error(`Error: ${response.statusText}`);
}

function createError(type: string, message: any): Error {
	if (type === 'DomainError')
		return new Errors.DomainError(message.Message);

	if (type === 'EmptyTicketError')
		return new Errors.EmptyTicketError(message.TicketId, message.Message);

	if (type === 'ExcelIntegrationError')
		return new Errors.ExcelIntegrationError(message.Message);

	if (type === 'FrozenDeliveryNoteError')
		return new Errors.FrozenDeliveryNoteError(message.NoteId, message.Message);

	if (type === 'FrozenPaymentsError')
		return new Errors.FrozenPaymentsError(message.Message);

	if (type === 'FrozenSalesOrderError')
		return new Errors.FrozenSalesOrderError(message.OrderId, message.Message);

	if (type === 'FrozenTicketError')
		return new Errors.FrozenTicketError(message.TicketId, message.Message);

	if (type === 'InvalidCustomerCifError')
		return new Errors.InvalidCustomerCifError(message.Message);

	if (type === 'InvalidPaymentError')
		return new Errors.InvalidPaymentError(message.Message);

	if (type === 'InvalidPriceListError')
		return new Errors.InvalidPriceListError(message.Message);

	if (type === 'InvalidTicketLineStateError')
		return new Errors.InvalidTicketLineStateError(message.LineId, message.LineStatus, message.Message);

	if (type === 'LicenseError')
		return new Errors.LicenseError(message.ErrorType, message.Message);

	if (type === 'PrintError')
		return new Errors.PrintError(message.Message);

	if (type === 'ProductNotAvailableError')
		return new Errors.ProductNotAvailableError(message.Message);

	if (type === 'ServerError')
		return new Errors.ServerError(message.Message);

	if (type === 'SyncError')
		return new Errors.SyncError(message.Code, message.Message);

	if (type === 'TicketLineNotFoundError')
		return new Errors.TicketLineNotFoundError(message.TicketId, message.TicketLineId, message.TicketLineGlobalId, message.Message);

	if (type === 'TicketNotFoundError')
		return new Errors.TicketNotFoundError(message.TicketId, message.TicketGlobalId, message.Message);

	if (type === 'TicketSynchronizationError')
		return new Errors.TicketSynchronizationError(message.TicketId, message.Message);

	if (type === 'VersionCompatibilityError')
		return new Errors.VersionCompatibilityError(message.Message);

	if (type === 'DeliveryNoteNotFoundError')
		return new Errors.DeliveryNoteNotFoundError(message.NoteId, message.Message);

	if (type === 'IntegrationAuthorizationError')
		return new Errors.IntegrationAuthorizationError(message.ErrorType, message.Message);

	return new Error(message.Message);
}

export function getPendingRequests(): AbstractMessage[] {
	return pendingRequests;
}

export function waitForPendingRequests(): Promise<void> {

	if (pendingRequests.length === 0)
		return Promise.resolve();

	const check = (resolve: () => void) => {
		if (pendingRequests.length === 0) {
			resolve();
		}
		else {
			setTimeout(() => check(resolve), 5);
		}
	};

	return new Promise(resolve => {
		check(resolve);
	});
}


const discardResponse: (response: any) => any = () => undefined;

export function buildProcessFn(endpoint: string): <T>(request: AbstractMessage, convert?: (response: any) => T, signal?: AbortSignal) => Promise<T> {
	return function <T>(request: AbstractMessage, convertResponse?: (response: any) => T, signal?: AbortSignal): Promise<T> {
		return process<T>(endpoint, request, convertResponse || discardResponse, signal);
	};
}

export function init(busConfig: BusConfig) {
	config = busConfig;
}

export function updateConfig(busConfig: Partial<BusConfig>) {

	if (config === null)
		throw Error('El Bus no ha sido inicializado');

	config = { ...config, ...busConfig };
}

export function getConfig() {
	if (config === null)
		throw Error('El Bus no ha sido inicializado');
	return config;
}

